import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { isToday } from "date-fns"
import {
  ArrowRight,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  Loader2,
} from "lucide-react"
import { useMemo } from "react"
import { Link } from "react-router"
import { NOTIFICATION_CENTER_ROUTE } from "../constants"
import { useMarkAllRead } from "../hooks/useMarkRead"
import { useNotifications } from "../hooks/useNotifications"
import { useUnreadCount } from "../hooks/useUnreadCount"
import type { NotificationDTO } from "../types"
import { NotificationItem } from "./NotificationItem"

interface NotificationListProps {
  open: boolean
  /** Close the popover (called before navigating away). */
  onNavigateAway?: () => void
}

const isCreatedToday = (createdAt: Date | string): boolean => {
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt)
  return !isNaN(date.getTime()) && isToday(date)
}

export const GroupEyebrow: React.FC<{ label: string }> = ({ label }) => (
  <p className="px-4 pb-1 pt-3 text-xs uppercase tracking-[0.08em] text-muted-foreground">
    {label}
  </p>
)

const SkeletonRows: React.FC = () => (
  <div data-testid="notification-skeleton" className="flex flex-col gap-1 p-2">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className="flex items-start gap-3 rounded-[10px] py-2 pl-[18px] pr-4"
      >
        <div className="h-8 w-8 shrink-0 animate-pulse rounded-[10px] bg-white/6 motion-reduce:animate-none" />
        <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
          <div className="h-3 w-3/4 animate-pulse rounded bg-white/6 motion-reduce:animate-none" />
          <div className="h-2.5 w-1/2 animate-pulse rounded bg-white/4 motion-reduce:animate-none" />
        </div>
      </div>
    ))}
  </div>
)

export const NotificationList: React.FC<NotificationListProps> = ({
  open,
  onNavigateAway,
}) => {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useNotifications({ enabled: open })
  const { data: unreadCount } = useUnreadCount()
  const { mutate: markAllRead } = useMarkAllRead()
  const { translate } = useTranslation()

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  )

  const { todayNotifications, earlierNotifications } = useMemo(() => {
    return {
      todayNotifications: notifications.filter((notification) =>
        isCreatedToday(notification.createdAt),
      ),
      earlierNotifications: notifications.filter(
        (notification) => !isCreatedToday(notification.createdAt),
      ),
    }
  }, [notifications])

  const hasUnread =
    (unreadCount?.count ?? 0) > 0 ||
    notifications.some((notification) => notification.readAt === null)

  const renderGroup = (label: string, items: NotificationDTO[]) => {
    if (items.length === 0) {
      return null
    }

    return (
      <div className="flex flex-col">
        <GroupEyebrow label={label} />
        {items.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            variant="popover"
            onNavigateAway={onNavigateAway}
          />
        ))}
      </div>
    )
  }

  const showFooter = !isLoading && !isError

  return (
    <div className="flex max-h-[480px] flex-col">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <p className="text-sm font-semibold text-foreground">
          {translate("NOTIF/UI/HEADER")}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          disabled={!hasUnread}
          onClick={() => markAllRead()}
        >
          <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {translate("NOTIF/UI/MARK_ALL_READ")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto pb-2">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              {translate("NOTIF/UI/LOAD_ERROR")}
            </p>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              {translate("NOTIF/UI/RETRY")}
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/6">
              <CheckCircle2
                className="h-5 w-5 text-muted-foreground"
                aria-hidden="true"
              />
            </span>
            <p className="text-sm text-muted-foreground">
              {translate("NOTIF/UI/EMPTY_CAUGHT_UP")}
            </p>
          </div>
        ) : (
          <>
            {renderGroup(translate("NOTIF/UI/GROUP_TODAY"), todayNotifications)}
            {renderGroup(
              translate("NOTIF/UI/GROUP_EARLIER"),
              earlierNotifications,
            )}
            {hasNextPage ? (
              <div className="px-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-1.5 text-xs text-muted-foreground"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage ? (
                    <Loader2
                      className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {isFetchingNextPage
                    ? translate("NOTIF/UI/LOADING")
                    : translate("NOTIF/UI/LOAD_MORE")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {showFooter ? (
        <div className="border-t border-border px-4 py-2.5">
          <Link
            to={NOTIFICATION_CENTER_ROUTE}
            onClick={() => onNavigateAway?.()}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {translate("NOTIF/UI/OPEN_CENTER")}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      ) : null}
    </div>
  )
}
